import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import restro from "../../../assets/orderingSystem/restrologo.png";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  addItem,
  increaseQty,
  decreaseQty,
  clearCart as clearPublicCart,
} from "../../../slices/cart/publicOrderingCartSlice";
import Header from "../../PublicOrdering/pages/Header";
import OrderTracking from "../../PublicOrdering/pages/OrderTracking";
import OrderDetailsModal from "../../PublicOrdering/pages/OrderDetailsModal";
import CartSummary from "../../PublicOrdering/pages/CartSummary";
import toast from "react-hot-toast";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import DesktopCartSidebar from "../../PublicOrdering/pages/DesktopCartSidebar";
import playBeep from "../../PublicOrdering/pages/PlayBeep";
import ThemeFooter from "../component/ThemeFooter";

// ============= SEARCH BAR WITH CART BUTTON COMPONENT =============
const SearchBarWithCart = ({
  menuItems,
  onSearch,
  totalItems,
  onCartClick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 992); // 992px for lg breakpoint

  const handleChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    onSearch(term);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="mb-3 px-2">
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {/* Search Input */}
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "12px 16px 12px 40px",
              borderRadius: "30px",
              border: "2px solid #e0e0e0",
              fontSize: "14px",
              backgroundColor: "#f9f9f9",
              transition: "all 0.3s ease",
              height: "36px",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#ff7900";
              e.target.style.boxShadow = "0 0 0 3px rgba(255, 121, 0, 0.15)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e0e0e0";
              e.target.style.boxShadow = "none";
            }}
          />
          <i
            className="bx bx-search"
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              color: "#999",
            }}
          />
        </div>

        {/* Cart Button */}
        {isSmallScreen && (
          <Button
            color="link"
            onClick={() => {
              playBeep();
              onCartClick();
            }}
            disabled={totalItems === 0} // optionally disable click when empty
            style={{
              position: "relative",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#ff7900",
              border: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                totalItems > 0 ? "0 2px 8px rgba(255, 121, 0, 0.3)" : "none",
              transition: "all 0.3s ease",
              flexShrink: 0,
              opacity: totalItems > 0 ? 1 : 0.5, // dim if empty
              cursor: totalItems > 0 ? "pointer" : "not-allowed",
            }}
            onMouseEnter={(e) => {
              if (totalItems > 0) {
                e.currentTarget.style.backgroundColor = "#e56a00";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (totalItems > 0) {
                e.currentTarget.style.backgroundColor = "#ff7900";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
          >
            <i
              className="bx bx-cart"
              style={{ fontSize: "22px", color: "#fff" }}
            />
            {totalItems > 0 && (
              <Badge
                color="danger"
                pill
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  fontSize: "9px",
                  padding: "3px 6px",
                  backgroundColor: "#dc3545",
                  fontWeight: "700",
                  minWidth: "18px",
                }}
              >
                {totalItems}
              </Badge>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

// ============= CATEGORIES SWIPER COMPONENT =============
const CategoriesSwiper = ({ categories, activeTab, setActiveTab }) => (
  <div className="mb-3 px-2">
    <Swiper
      modules={[FreeMode]}
      spaceBetween={12}
      slidesPerView="auto"
      freeMode={{
        enabled: true,
        momentum: true,
        momentumBounce: false,
        momentumRatio: 0.9,
      }}
      grabCursor={true}
      style={{ padding: "6px 0" }}
    >
      {categories.map((cat) => (
        <SwiperSlide key={cat.id} style={{ width: "auto" }}>
          <Button
            size="sm"
            className="text-nowrap"
            style={{
              borderRadius: "20px",
              padding: "6px 16px",
              fontWeight: activeTab === cat.id ? "600" : "500",
              fontSize: "12px",
              border: activeTab === cat.id ? "none" : "1px solid #e0e0e0",
              backgroundColor: activeTab === cat.id ? "#ff7900" : "#fff",
              color: activeTab === cat.id ? "#fff" : "#495057",
              boxShadow:
                activeTab === cat.id
                  ? "0 2px 6px rgba(255, 121, 0, 0.25)"
                  : "none",
            }}
            onClick={() => {
              playBeep();
              setActiveTab(cat.id);
            }}
          >
            {cat.name}
          </Button>
        </SwiperSlide>
      ))}
    </Swiper>
  </div>
);

// ============= MENU ITEM CARD COMPONENT =============
const MenuItemCard = ({ item, cartItem, onAdd, onRemove, onCardClick }) => (
  <Col xs={6} sm={6} md={3} lg={3}>
    <Card
      className="border-0"
      style={{
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        cursor: "pointer",
        padding: "6px",
      }}
      onClick={() => onCardClick && onCardClick(item)}
    >
      {/* Image Section */}
      <div
        style={{
          height: "90px",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <img
          src={item.displayImage?.getCDNUrl() || restro}
          alt={item.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            padding: "5px",
          }}
          onError={(e) => (e.target.src = restro)}
        />
        {item.discount && (
          <Badge
            color="success"
            className="position-absolute"
            style={{
              top: "5px",
              right: "5px",
              fontSize: "8px",
              padding: "2px 5px",
            }}
          >
            {item.discount}
          </Badge>
        )}
      </div>

      {/* Card Body */}
      <CardBody
        style={{
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        {/* Product Name */}
        <CardTitle
          tag="h6"
          className="text-center mb-0"
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#2c3e50",
            lineHeight: "1.2",
            height: "28px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {item.name}
        </CardTitle>

        {/* Price + Eye Button */}
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <span
              className="fw-bold"
              style={{ fontSize: "14px", color: "#dc3545" }}
            >
              Rs. <NumberWithCommas number={item.price} />
            </span>
            {item.originalPrice && item.originalPrice > item.price && (
              <del className="ms-1" style={{ fontSize: "12px", color: "#aaa" }}>
                Rs. <NumberWithCommas number={item.originalPrice} />
              </del>
            )}
          </div>
          <button
            className="btn btn-soft-success btn-sm me-2"
            onClick={(e) => {
              playBeep();
              e.stopPropagation();
              onAdd(item);
            }}
          >
            <i className="ri-eye-2-line"></i>
          </button>
        </div>
      </CardBody>
    </Card>
  </Col>
);

// ============= MENU GRID COMPONENT =============
const MenuGrid = ({ filteredItems, cart, onAdd, onRemove, onCardClick }) => {
  if (filteredItems.length === 0) {
    return (
      <div className="text-center py-5">
        <i
          className="bx bx-food-menu"
          style={{ fontSize: "48px", color: "#ccc" }}
        />
        <p className="text-muted mt-2" style={{ fontSize: "13px" }}>
          No items available
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        maxHeight: "calc(100vh - 30vh)",
        overflowY: "auto",
        paddingRight: "8px",
        overflowX: "hidden",
      }}
    >
      <Row className="">
        {filteredItems.map((item) => {
          const cartItem = cart.find((i) => i.id === item.id);
          return (
            <MenuItemCard
              key={item.id}
              item={item}
              cartItem={cartItem}
              onAdd={onAdd}
              onRemove={onRemove}
              onCardClick={onCardClick}
            />
          );
        })}
      </Row>
    </div>
  );
};

// ============= SCROLL TO TOP BUTTON COMPONENT =============
// const ScrollToTopButton = ({ show, onClick }) => {
//   if (!show) return null;

//   return (
//     <Button
//       color="danger"
//       onClick={onClick}
//       style={{
//         position: "fixed",
//         bottom: "20px",
//         right: "20px",
//         borderRadius: "50%",
//         width: "50px",
//         height: "50px",
//         zIndex: 9999,
//         backgroundColor: "#ff7900",
//         border: "none",
//       }}
//     >
//       ↑
//     </Button>
//   );
// };

// ============= MAIN COMPONENT =============
export const ThemeMenu2 = ({ orgInfo, orgData }) => {
  console.log("🚀 ~ ThemeMenu2 ~ orgInfo:", orgInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Use publicOrderingCart slice
  const publicCartState = useSelector(
    (s) =>
      s.publicOrderingCart || {
        items: [],
        totalQty: 0,
        totalAmount: 0,
      }
  );
  const orgCode = orgData?.orgCode;
  console.log("🚀 ~ ThemeMenu2 ~ orgCode:", orgCode);
  const cart = publicCartState.items || [];
  const [activeTab, setActiveTab] = useState("all");
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showScroll, setShowScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [productNotes, setProductNotes] = useState("");
  const [productQty, setProductQty] = useState(1);
  // State for offcanvas
  const [cartOffcanvasOpen, setCartOffcanvasOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgInfo && orgData) {
      // Simulate fetch delay
      const timer = setTimeout(() => setLoading(false), 800); // 0.8s for demo
      return () => clearTimeout(timer);
    }
  }, [orgInfo, orgData]);

  useEffect(() => {
    const data = orgInfo;
    const info = orgData;

    const catKeys = Object.keys(data);
    const formattedCategories = catKeys.map((key) => ({
      id: key,
      name: key,
    }));
    setCategories([{ id: "all", name: "All Items" }, ...formattedCategories]);

    const items = [];
    catKeys.forEach((key) => {
      data[key].forEach((item) => {
        items.push({ ...item, category: key });
      });
    });
    setMenuItems(items);
  }, [orgInfo, orgData]);
  // // ============= SOUND BEEP FUNCTION ====================
  // const playBeep = () => {
  //   try {
  //     // public/ files are served from the root URL, so use `/beep.mp3` (not `/public/beep.mp3`)
  //     const audio = new Audio("/beep1.mp3");
  //     audio.volume = 0.6; // 0 to 1
  //     // handle the promise returned by play() to catch any playback rejection
  //     const playPromise = audio.play();
  //     if (playPromise && typeof playPromise.catch === "function") {
  //       playPromise.catch((err) => {
  //         console.log("Beep playback rejected:", err);
  //       });
  //     }
  //   } catch (err) {
  //     console.error("Beep sound error:", err);
  //   }
  // };

  const addToCart = (item) => {
    // open product modal to allow notes/addons selection
    setSelectedAddons([]);
    setProductNotes("");
    setProductQty(1);
    setSelectedProduct(item);
    // Use setTimeout to ensure state is set before opening modal
    setTimeout(() => {
      setProductModalOpen(true);
    }, 0);
  };

  const addImmediateToCart = (item) => {
    // add without customization: try to find exact match (no addons/attributes)
    const entry = cart.find(
      (c) =>
        c.productId === item.id &&
        (!c.addons || c.addons.length === 0) &&
        (!c.attributes || c.attributes.length === 0)
    );
    if (entry) {
      dispatch(increaseQty(entry.uid));
      // toast.success(`Increased quantity of ${item.name}in cart`);
    } else {
      dispatch(
        addItem({
          id: item.id,
          name: item.name,
          basePrice: item.price || item.basePrice || 0,
          attributes: item.attributes || {},
          addons: [],
          extras: [],
        })
      );
    }
    playBeep();
    toast.success(`Added ${item.name} to cart`);
  };

  const toggleProductModal = () => setProductModalOpen((s) => !s);
  const toggleCartOffcanvas = () => {
    // Only allow offcanvas on small screens (< 992px)
    if (window.innerWidth < 992) {
      setCartOffcanvasOpen((s) => !s);
    }
  };
  const handleToggleAddon = (addonIndex) => {
    setSelectedAddons((prev) => {
      if (prev.includes(addonIndex))
        return prev.filter((i) => i !== addonIndex);
      return [...prev, addonIndex];
    });
  };

  const handleConfirmAddToCart = () => {
    if (!selectedProduct) return;

    const addons = (selectedProduct.addons || []).filter((_, idx) =>
      selectedAddons.includes(idx)
    );

    const payload = {
      id: selectedProduct.id,
      name: selectedProduct.name,
      basePrice: selectedProduct.price || selectedProduct.basePrice || 0,
      attributes: selectedProduct.attributes || {},
      addons: addons,
      extras: selectedProduct.extras || [],
      note: productNotes,
    };

    playBeep();
    // Dispatch addItem productQty times (slice stores quantity=1 per add)
    for (let i = 0; i < (productQty || 1); i++) {
      dispatch(addItem(payload));
    }
    toast.success("Order added to cart");
    // close modal
    setProductModalOpen(false);
    setSelectedProduct(null);
    setSelectedAddons([]);
    setProductNotes("");
    setProductQty(1);
  };

  const filteredItems = useMemo(() => {
    let items =
      activeTab === "all"
        ? menuItems
        : menuItems.filter((item) => item.category === activeTab);

    if (searchTerm.trim()) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return items;
  }, [activeTab, menuItems, searchTerm]);

  const totalItems =
    publicCartState.totalQty || cart.reduce((s, i) => s + (i.qty || 0), 0);
  const totalPrice =
    publicCartState.totalAmount ||
    cart.reduce(
      (s, i) => s + (i.totalPrice || i.basePrice || 0) * (i.qty || 0),
      0
    );

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const customer = JSON.parse(localStorage.getItem("CustomerInfo"));
  const isLoggedIn = customer?.accessToken;

  const handleOrderSubmit = (formData) => {
    // Build order items using the product grid data + cart entries
    const orderItems = (cart || []).map((ci) => {
      const product = menuItems.find((m) => m.id === ci.id) || {};
      const unitPrice = ci.basePrice || product.price || product.basePrice || 0;
      const qty = ci.qty || ci.quantity || 0;
      return {
        id: ci.id,
        name: ci.name || product.name || "",
        quantity: qty,
        price: unitPrice,
        addons: ci.addons || [],
        extras: ci.extras || [],
        attributes: ci.attributes || {},
        total: (ci.totalPrice || unitPrice) * qty,
      };
    });

    const computedTotal = orderItems.reduce((s, it) => s + (it.total || 0), 0);

    const order = {
      orderId: `ORD-${Date.now()}`,
      items: orderItems,
      total: computedTotal,
      totalItems: orderItems.reduce((s, it) => s + it.quantity, 0),
      customer: formData,
      status: "Received",
      placedTime: new Date().toLocaleString(),
      orgCode: orgInfo?.orgCode || orgData?.orgCode,
    };

    // If user not logged in, save pending order to localStorage and redirect to login
    if (!isLoggedIn) {
      try {
        localStorage.setItem("pendingOrder", JSON.stringify(order));
      } catch (err) {
        console.error("Error saving pending order:", err);
      }
      dispatch(clearPublicCart());
      setModalOpen(false);
      const orgCode = orgInfo?.orgCode || orgData?.orgCode;
      navigate(`/${orgCode}/login`);
      return;
    }

    // If logged in, place order immediately (localStorage-based)
    try {
      const stored = localStorage.getItem("placedOrders");
      const arr = stored ? JSON.parse(stored) : [];
      arr.push(order);
      localStorage.setItem("placedOrders", JSON.stringify(arr));
      localStorage.setItem("lastPlacedOrder", JSON.stringify(order));
    } catch (err) {
      console.error("Error placing order:", err);
    }

    dispatch(clearPublicCart());
    setModalOpen(false);
    const orgCode = orgInfo?.orgCode || orgData?.orgCode;
    navigate(`/${orgCode}/order-summary`, {
      state: { orgData: orgData },
    });
  };

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        // minHeight: "100vh",
        // paddingBottom: "20px",
      }}
    >
      <Container
        style={{
          maxWidth: "1200px",
        }}
      >
        <div className="sticky-top bg-light">
          <Header orgData={orgData} orgcode={orgCode} />
          <Row>
            <Col md={3}>
              {loading ? (
                <Skeleton height={36} borderRadius={30} />
              ) : (
                <SearchBarWithCart
                  menuItems={menuItems}
                  onSearch={setSearchTerm}
                  totalItems={totalItems}
                  onCartClick={toggleCartOffcanvas}
                />
              )}
            </Col>
            <Col md={9}>
              {loading ? (
                <div className="d-flex gap-2 px-2">
                  {[...Array(10)].map((_, idx) => (
                    <Skeleton
                      key={idx}
                      width={80}
                      height={30}
                      borderRadius={20}
                    />
                  ))}
                </div>
              ) : (
                <CategoriesSwiper
                  categories={categories}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              )}
            </Col>
          </Row>
        </div>
        <Row style={{ maxHeight: "50vh" }}>
          <Col xs={12} lg={8}>
            {loading ? (
              <Row>
                {[...Array(16)].map((_, idx) => (
                  <Col xs={6} sm={6} md={3} lg={3} key={idx}>
                    <Card
                      className="border-0"
                      style={{ borderRadius: 10, padding: 6 }}
                    >
                      <Skeleton height={90} borderRadius={6} />
                      <CardBody style={{ padding: 8 }}>
                        <Skeleton
                          height={14}
                          width="80%"
                          style={{ marginBottom: 6 }}
                        />
                        <Skeleton height={14} width="60%" />
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <MenuGrid
                filteredItems={filteredItems}
                cart={cart.map((c) => ({
                  id: c.productId || c.id,
                  quantity: c.quantity || c.qty,
                }))}
                onAdd={(item) => addToCart(item)}
                onRemove={() => {}}
                onCardClick={(item) => addImmediateToCart(item)}
              />
            )}
          </Col>

          <Col
            lg={4}
            className="d-none d-lg-block d-md-block  "
            // style={{ top: "3vh" }}
          >
            <div
              style={{
                position: "sticky",
                // top: "10vh",
                // height: "fit-content",
                // maxHeight: "100vh",
                height: "calc(100vh - 35vh)",

                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                border: "none",
                overflow: "hidden",
              }}
            >
              <DesktopCartSidebar
                cart={cart}
                orgInfo={orgData}
                orgData={orgData}
              />
            </div>
          </Col>
        </Row>
        {/* <Row>
   <div className="sticky-bottom bg-success">
        <ThemeFooter />
      </div>
</Row> */}
        {/* Floating My Orders Button */}
        <Button
          color="link"
          onClick={() => {
            playBeep();
            setTrackingOpen(true);
          }}
          style={{
            position: "fixed",
            right: "20px",
            bottom: "80px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#ff7900",
            border: "none",
            padding: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
            boxShadow: "0 4px 12px rgba(255, 121, 0, 0.3)",
            transition: "all 0.3s ease",
          }}
        >
          <i
            className="bx bx-receipt"
            style={{ fontSize: "24px", color: "#fff" }}
          />
        </Button>
      </Container>

      {/* <ScrollToTopButton
        show={showScroll}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      /> */}

      {cart.length > 0 && (
        <>
          {/* Cart Offcanvas */}
          <CartSummary
            cart={cart}
            onClear={() => dispatch(clearPublicCart())}
            onOrderNow={() => setModalOpen(true)}
            orgInfo={orgData}
            orgData={orgData}
            isOpen={cartOffcanvasOpen}
            toggle={toggleCartOffcanvas}
          />

          <OrderDetailsModal
            isOpen={modalOpen}
            toggle={() => setModalOpen(false)}
            onSubmit={handleOrderSubmit}
            totalPrice={totalPrice}
            totalItems={totalItems}
          />
        </>
      )}

      {/* Product Options Modal */}
      <Modal
        isOpen={productModalOpen && selectedProduct !== null}
        toggle={toggleProductModal}
        centered
      >
        <ModalHeader
          toggle={toggleProductModal}
          style={{
            // backgroundColor: "#ff7900", // Brand/orange color
            // color: "white", // White text for contrast
            fontWeight: "700", // Bold title
            fontSize: "20px", // Slightly larger font
            borderBottom: "1px solid #000", // subtle bottom border
            borderRadius: "8px 8px 0 0", // rounded top corners
            padding: "12px 16px", // extra padding for better look
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {selectedProduct?.name || "Customize Item"}
        </ModalHeader>
        <ModalBody>
          {selectedProduct && (
            <div>
              <div className="d-flex gap-3 mb-3">
                <img
                  // src={selectedProduct.image || restro}
                  src={selectedProduct.displayImage?.getCDNUrl() || restro}
                  alt={selectedProduct.name}
                  style={{
                    width: 90,
                    height: 90,
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: "16px",
                        color: "#ff7900",
                        marginBottom: "4px",
                      }}
                    >
                      Rs. <NumberWithCommas number={selectedProduct.price} />
                    </div>
                    {selectedProduct.originalPrice && (
                      <del style={{ fontSize: 11, color: "#aaa" }}>
                        Rs.{" "}
                        <NumberWithCommas
                          number={selectedProduct.originalPrice}
                        />
                      </del>
                    )}
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      Subtotal:{" "}
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#ff7900",
                          fontSize: "14px",
                        }}
                      >
                        Rs.{" "}
                        <NumberWithCommas
                          number={selectedProduct.price * productQty}
                        />
                      </span>
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <button
                      onClick={() => {
                        playBeep();
                        setProductQty(Math.max(1, productQty - 1));
                      }}
                      disabled={productQty <= 1}
                      style={{
                        width: "24px",
                        height: "24px",
                        border: "1px solid",
                        borderColor: productQty <= 1 ? "#ddd" : "#ff7900",
                        borderRadius: "6px",
                        background: productQty <= 1 ? "#f5f5f5" : "white",
                        color: productQty <= 1 ? "#999" : "#ff7900",
                        cursor: productQty <= 1 ? "not-allowed" : "pointer",
                        fontSize: "16px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      −
                    </button>

                    <div
                      style={{
                        minWidth: "28px",
                        textAlign: "center",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {productQty}
                    </div>

                    <button
                      onClick={() => {
                        playBeep();
                        setProductQty(productQty + 1);
                      }}
                      style={{
                        width: "24px",
                        height: "24px",
                        border: "none",
                        borderRadius: "6px",
                        background: "#ff7900",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Addons Section (if any) */}
              {(selectedProduct.addons || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Addons</div>
                  <div>
                    {(selectedProduct.addons || []).map((a, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginBottom: 6,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAddons.includes(idx)}
                          onChange={() => handleToggleAddon(idx)}
                          style={{ cursor: "pointer" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{a.name}</div>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            Rs. <NumberWithCommas number={a.price} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <FormGroup>
                <Label for="notes">Notes</Label>
                <Input
                  type="textarea"
                  id="notes"
                  value={productNotes}
                  onChange={(e) => setProductNotes(e.target.value)}
                  placeholder="e.g., No onions, extra spicy, add cheese"
                  style={{ minHeight: 80 }}
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              playBeep();
              toggleProductModal();
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              playBeep();
              handleConfirmAddToCart();
            }}
            style={{ backgroundColor: "#ff7900", border: "none" }}
          >
            Add to Cart
          </Button>
        </ModalFooter>
      </Modal>

      <OrderTracking
        isOpen={trackingOpen}
        toggle={() => setTrackingOpen(false)}
        orgData={orgData}
      />
    </div>
  );
};

export default ThemeMenu2;
